import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api';
import {
  formatCasePriority,
  formatCaseStatus,
  isActiveSupportCase,
  isCriticalSupportCase,
  isStaleSupportCase,
} from '../utils/supportSignals';

export default function SupportInbox() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [supportCases, setSupportCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workingKey, setWorkingKey] = useState('');

  async function loadCases() {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/admin/platform/support-cases');
      setSupportCases(response.data.supportCases || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load support cases');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCases();
  }, []);

  useEffect(() => {
    const refresh = () => loadCases();
    window.addEventListener('support-cases:updated', refresh);
    return () => window.removeEventListener('support-cases:updated', refresh);
  }, []);

  async function assignToMe(id) {
    const key = `assign:${id}`;
    setWorkingKey(key);
    try {
      await api.post(`/admin/platform/support-cases/${id}/assign`);
      await loadCases();
      window.dispatchEvent(new Event('support-cases:updated'));
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not assign this support case');
    } finally {
      setWorkingKey('');
    }
  }

  async function updateStatus(id, status) {
    const key = `status:${id}:${status}`;
    setWorkingKey(key);
    try {
      await api.post(`/admin/platform/support-cases/${id}/status`, { status });
      await loadCases();
      window.dispatchEvent(new Event('support-cases:updated'));
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not update this support case');
    } finally {
      setWorkingKey('');
    }
  }

  const summary = useMemo(() => {
    const activeCases = supportCases.filter(isActiveSupportCase);
    return {
      total: supportCases.length,
      active: activeCases.length,
      critical: activeCases.filter(isCriticalSupportCase).length,
      stale: activeCases.filter((item) => isStaleSupportCase(item)).length,
      waiting: activeCases.filter((item) => item.status === 'waiting_customer').length,
    };
  }, [supportCases]);

  const filters = useMemo(
    () => ({
      status: searchParams.get('status') || '',
      priority: searchParams.get('priority') || '',
      view: searchParams.get('view') || '',
    }),
    [searchParams]
  );

  const visibleCases = useMemo(() => {
    return supportCases.filter((item) => {
      if (filters.status && item.status !== filters.status) return false;
      if (filters.priority && item.priority !== filters.priority) return false;
      if (filters.view === 'stale' && !isStaleSupportCase(item)) return false;
      if (filters.view === 'waiting' && item.status !== 'waiting_customer') return false;
      if (filters.view === 'critical' && !isCriticalSupportCase(item)) return false;
      if (filters.view === 'active' && !isActiveSupportCase(item)) return false;
      return true;
    });
  }, [filters, supportCases]);

  const hasFilters = Boolean(filters.status || filters.priority || filters.view);

  const setFilter = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next);
  };

  return (
    <Layout searchPlaceholder="Search support queue filters...">
      <div className="space-y-8">
        <section className="rounded-[2.2rem] border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top_left,rgba(245,178,33,0.22),transparent_36%),linear-gradient(180deg,#111111,#020202)] p-6 shadow-[0_24px_70px_rgba(0,0,0,0.4)] sm:p-8">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-4xl">
              <p className="text-sm font-bold uppercase tracking-[0.28em] text-[#f7d88a]">
                Support Inbox
              </p>
              <h1 className="mt-3 text-3xl font-black tracking-[-0.03em] text-white sm:text-4xl">
                Review customer cases from one clear admin queue.
              </h1>
              <p className="mt-3 max-w-4xl text-sm leading-7 text-[#cfcfcf]">
                Open the full review workspace for any case, assign ownership fast, and filter down to critical or stale requests without mixing this queue with moderation work.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={loadCases}
                className="rounded-full border border-[#f5b221]/28 px-5 py-3 text-sm font-bold text-[#f7d88a] transition hover:bg-[#f5b221]/10"
              >
                Refresh queue
              </button>
              <button
                type="button"
                onClick={() => navigate('/command-alerts')}
                className="rounded-full bg-[#f5b221] px-5 py-3 text-sm font-bold text-black transition hover:bg-[#f7d88a]"
              >
                Open command alerts
              </button>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-5">
          <MetricCard title="Total cases" value={summary.total} body="All loaded support requests" />
          <MetricCard title="Active" value={summary.active} body="Open, in progress, or waiting customer" />
          <MetricCard title="Critical" value={summary.critical} body="Priority cases needing fast review" />
          <MetricCard title="Stale" value={summary.stale} body="Open cases with no recent movement" />
          <MetricCard title="Waiting" value={summary.waiting} body="Cases paused on customer follow-up" />
        </section>

        {error ? (
          <div className="rounded-[1.8rem] border border-red-500/25 bg-red-950/15 px-5 py-4 text-sm text-red-200">
            {error}
          </div>
        ) : null}

        <section className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-2xl font-black tracking-[-0.03em] text-white">
                Live review queue
              </h2>
              <p className="mt-2 text-sm text-[#b8b8b8]">
                Filter for urgent work, then open any case into the full review page for detailed adjustments.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <FilterChip
                active={filters.view === 'active'}
                onClick={() => setFilter({ view: filters.view === 'active' ? '' : 'active' })}
                label="Active only"
              />
              <FilterChip
                active={filters.priority === 'critical'}
                onClick={() =>
                  setFilter({ priority: filters.priority === 'critical' ? '' : 'critical' })
                }
                label="Critical"
              />
              <FilterChip
                active={filters.view === 'stale'}
                onClick={() => setFilter({ view: filters.view === 'stale' ? '' : 'stale' })}
                label="Stale"
              />
              <FilterChip
                active={filters.view === 'waiting'}
                onClick={() => setFilter({ view: filters.view === 'waiting' ? '' : 'waiting' })}
                label="Waiting customer"
              />
              {hasFilters ? (
                <button
                  type="button"
                  onClick={() => setSearchParams(new URLSearchParams())}
                  className="rounded-full border border-[#4e4632] px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#d2c5ab] transition hover:bg-[#111111]"
                >
                  Clear filters
                </button>
              ) : null}
            </div>
          </div>

          {loading ? (
            <div className="mt-6 rounded-[1.6rem] border border-dashed border-[#2b2b2b] bg-black/40 p-5 text-sm text-[#989898]">
              Loading support queue...
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              {!visibleCases.length ? (
                <div className="rounded-[1.6rem] border border-dashed border-[#2b2b2b] bg-black/40 p-5 text-sm text-[#989898]">
                  {hasFilters
                    ? 'No support cases match the current filters.'
                    : 'No support requests are waiting right now.'}
                </div>
              ) : (
                visibleCases.map((item) => {
                  const assignedLabel = item.assignedAdminId
                    ? `${item.assignedAdminId.firstName || ''} ${item.assignedAdminId.lastName || ''}`.trim() ||
                      item.assignedAdminId.email
                    : 'Nobody yet';

                  return (
                    <div key={item._id} className="rounded-[1.8rem] border border-[#f5b221]/10 bg-black/60 p-5">
                      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                        <div className="max-w-3xl">
                          <button
                            type="button"
                            onClick={() => navigate(`/support/${item._id}`)}
                            className="text-left text-lg font-bold text-white transition hover:text-[#f7d88a]"
                          >
                            {item.title}
                          </button>
                          <p className="mt-2 text-sm text-[#9f9f9f]">
                            {(item.organizationId && item.organizationId.name) || 'Unassigned org'} ·{' '}
                            {(item.systemId && item.systemId.name) || item.sourceSystemKey || 'No system'}
                          </p>
                          {item.summary ? (
                            <p className="mt-4 rounded-[1.2rem] bg-[#111111] px-4 py-3 text-sm text-[#c9c9c9]">
                              {item.summary}
                            </p>
                          ) : null}
                        </div>

                        <div className="flex flex-wrap gap-2 text-xs font-bold uppercase tracking-[0.18em]">
                          <Pill tone="status">{formatCaseStatus(item.status)}</Pill>
                          <Pill tone={item.priority === 'critical' ? 'critical' : 'priority'}>
                            {formatCasePriority(item.priority)}
                          </Pill>
                          {isStaleSupportCase(item) ? <Pill tone="warning">Stale</Pill> : null}
                        </div>
                      </div>

                      <div className="mt-4 grid gap-3 text-sm text-[#b6b6b6] sm:grid-cols-2 xl:grid-cols-4">
                        <InfoField
                          label="Requested by"
                          value={item.requestedBy?.name || item.requestedBy?.email || item.requestedBy?.shimbirrId || 'Unknown'}
                        />
                        <InfoField label="Partner" value={item.sourcePartner || 'N/A'} />
                        <InfoField label="Assigned" value={assignedLabel} />
                        <InfoField
                          label="Opened"
                          value={item.openedAt ? new Date(item.openedAt).toLocaleString() : 'Unknown'}
                        />
                      </div>

                      <div className="mt-4 flex flex-wrap gap-3">
                        <ActionButton
                          label="Open review"
                          busy={false}
                          onClick={() => navigate(`/support/${item._id}`)}
                          tone="solid"
                        />
                        <ActionButton
                          label="Assign to me"
                          busy={workingKey === `assign:${item._id}`}
                          onClick={() => assignToMe(item._id)}
                        />
                        <ActionButton
                          label="Waiting customer"
                          busy={workingKey === `status:${item._id}:waiting_customer`}
                          onClick={() => updateStatus(item._id, 'waiting_customer')}
                        />
                        <ActionButton
                          label="Mark resolved"
                          busy={workingKey === `status:${item._id}:resolved`}
                          onClick={() => updateStatus(item._id, 'resolved')}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

function MetricCard({ title, value, body }) {
  return (
    <div className="rounded-[1.8rem] border border-[#f5b221]/12 bg-[linear-gradient(180deg,#111111,#040404)] p-5">
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8f8f8f]">{title}</p>
      <p className="mt-3 text-4xl font-black tracking-[-0.04em] text-[#f5b221]">{value}</p>
      <p className="mt-2 text-sm text-[#b4b4b4]">{body}</p>
    </div>
  );
}

function FilterChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition',
        active
          ? 'bg-[#f5b221] text-black'
          : 'border border-[#4e4632] text-[#d2c5ab] hover:bg-[#111111]',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7f7f7f]">{label}</p>
      <p className="mt-1 text-white">{value}</p>
    </div>
  );
}

function Pill({ children, tone = 'status' }) {
  const classes = {
    status: 'bg-[#151515] text-[#f7d88a]',
    priority: 'bg-[#f5b221]/10 text-[#f5b221]',
    critical: 'bg-red-500/10 text-red-100',
    warning: 'bg-[#fd6c00]/10 text-[#ffcfb0]',
  };

  return (
    <span className={`rounded-full px-3 py-1 ${classes[tone] || classes.status}`}>
      {children}
    </span>
  );
}

function ActionButton({ label, busy, onClick, tone = 'outline' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={[
        'rounded-full px-4 py-2 text-sm font-bold transition disabled:opacity-60',
        tone === 'solid'
          ? 'bg-[#f5b221] text-black hover:bg-[#f7d88a]'
          : 'border border-[#333333] text-white hover:bg-[#111111]',
      ].join(' ')}
    >
      {busy ? 'Working...' : label}
    </button>
  );
}
